import { useCallback, useState } from 'react';
import { openToast, openToastError } from 'src/use-cases/toast/useToast';

/**
 * Generic type for entity mutation functions
 */
type EntityMutationFn<T> = (entity: T) => Promise<any>;

/**
 * Options for the entity handler hook
 */
interface EntityHandlerOptions<T> {
  // Entity type info
  entityName: string;

  // API functions
  upsertEntity: EntityMutationFn<T>;
  deleteEntity?: (params: any) => Promise<any>;

  // Entity preparation
  prepareEntityForUpsert: (values: any, originalData?: any) => T;
}

/**
 * A reusable hook for handling CRUD operations on table entities
 * Reduces duplication by standardizing create, update, and delete operations
 */
export function useTableEntityHandler<T extends Record<string, any>>(options: EntityHandlerOptions<T>, validateFn: (entity: any) => boolean) {
  const { entityName, upsertEntity, deleteEntity, prepareEntityForUpsert } = options;

  // Loading states for different operations
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Combined loading state
  const isLoading = isCreating || isUpdating || isDeleting;

  /**
   * Handles entity creation or update
   */
  const handleEntityAction = useCallback(
    async (params: { values: any; row?: any; exitEditingMode?: () => void; exitCreatingMode?: () => void }) => {
      const { values, row, exitEditingMode, exitCreatingMode } = params;

      // Determine if creating or updating
      const isCreatingAction = !row;
      const exitFn = isCreatingAction ? exitCreatingMode : exitEditingMode;
      const originalData = row?.original;

      // Validate the entity
      const isValid = validateFn(values);
      if (!isValid) return;

      try {
        // Set loading state based on operation type
        if (isCreatingAction) {
          setIsCreating(true);
        } else {
          setIsUpdating(true);
        }
        // Prepare the entity for upsert
        const reqBody = prepareEntityForUpsert(values, originalData);

        // Submit the upsert request
        await upsertEntity(reqBody);

        // Show success message
        openToast({
          toastType: 'success',
          title: 'Success',
          description: `${entityName} ${isCreatingAction ? 'created' : 'updated'} successfully!`,
        });
      } catch (error) {
        openToastError(new Error(`${entityName} ${isCreatingAction ? 'creation' : 'update'} failed`));
      } finally {
        // Reset loading state
        if (isCreatingAction) {
          setIsCreating(false);
        } else {
          setIsUpdating(false);
        }
        if (exitFn) exitFn();
      }
    },
    [entityName, prepareEntityForUpsert, upsertEntity, validateFn, setIsCreating, setIsUpdating]
  );

  /**
   * Create handler
   */
  const handleCreate = useCallback(
    ({ values, exitCreatingMode }) => {
      handleEntityAction({ values, exitCreatingMode });
    },
    [handleEntityAction]
  );

  /**
   * Update handler
   */
  const handleUpdate = useCallback(
    ({ values, row, exitEditingMode }) => {
      handleEntityAction({ values, row, exitEditingMode });
    },
    [handleEntityAction]
  );

  /**
   * Delete handler
   */
  const handleDelete = useCallback(
    async (row: { original: any }, options?: Record<string, any>) => {
      if (!deleteEntity) {
        openToastError(new Error(`Delete operation not supported for ${entityName}`));
        return;
      }

      if (!row.original.id) {
        openToastError(new Error(`Cannot delete: invalid ID`));
        return;
      }

      try {
        // Set deleting loading state
        setIsDeleting(true);
        // Check if we need to use an array of IDs or a single ID
        // This handles different API requirements
        const deleteParams = options?.useIdsArray
          ? { ids: [row.original.id], ...options?.additionalParams }
          : { id: row.original.id, ...options?.additionalParams };

        await deleteEntity(deleteParams);

        openToast({
          toastType: 'success',
          title: 'Success',
          description: `${entityName} deleted successfully!`,
        });
      } catch (error) {
        openToastError(new Error(`${entityName} deletion failed`));
      } finally {
        // Reset deleting loading state
        setIsDeleting(false);
      }
    },
    [deleteEntity, entityName]
  );

  return {
    handleEntityAction,
    handleCreate,
    handleUpdate,
    handleDelete,
    // Expose loading states
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
  };
}
